import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { Skeleton, Container, Typography, Button, Paper, Stack, Box } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useNavigate } from 'react-router-dom';

import { SortableList } from '../../components/sortableList';
import type { TestProps } from '../../models';
import { fCurrency } from '../../utils/formatNumber';
import { useGetTestsQuery, useOrderTestsMutation } from '../../slices/apis/testApi';

export default function TestsReorder() {
	const navigate = useNavigate();

	const [sortableTests, setSortableItems] = React.useState<TestProps[]>([]);

	const { data, isLoading } = useGetTestsQuery();
	const [orderTests, { isLoading: isOrderTestsLoading, isSuccess }] = useOrderTestsMutation();

	const handleUpdate = () => {
		orderTests({
			tests: sortableTests,
		});
	};

	const handleNavigate = React.useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			navigate(e.currentTarget.getAttribute('data-route') as string);
		},
		[navigate],
	);

	React.useEffect(() => {
		if (data && data.length > 0) {
			setSortableItems(data);
		}

		if (isSuccess) {
			navigate('/dashboard/admin/tests');
		}
	}, [data, navigate, isSuccess]);

	if (!data || isLoading) {
		return (
			<>
				<Helmet>
					<title>Tests Reorder - {process.env.REACT_APP_ORG}</title>
				</Helmet>
				<Container sx={{ width: '75%' }}>
					<Paper sx={{ p: 4, borderRadius: 3, boxShadow: 3 }}>
						<Stack spacing={3}>
							{[...Array(5)].map((_, index) => (
								<Box
									key={_}
									sx={{
										display: 'flex',
										justifyContent: 'space-between',
										alignItems: 'center',
									}}
								>
									<Box sx={{ flexGrow: 1 }}>
										<Skeleton variant="text" animation="wave" width="40%" height={28} />
										<Skeleton variant="text" animation="wave" width="60%" height={20} />
										<Skeleton variant="text" animation="wave" width="20%" height={22} />
									</Box>
									<Skeleton variant="circular" width={24} height={24} />
								</Box>
							))}
						</Stack>

						<Stack direction="row" spacing={4} sx={{ mt: 4 }}>
							<Skeleton variant="rectangular" animation="wave" width={100} height={36} />
							<Skeleton variant="rectangular" animation="wave" width={100} height={36} />
						</Stack>
					</Paper>
				</Container>
			</>
		);
	}

	return (
		<>
			<Helmet>
				<title>Tests Reorder - {process.env.REACT_APP_ORG}</title>
			</Helmet>
			<Container>
				<Paper sx={{ p: 4, borderRadius: 3, boxShadow: 3 }}>
					<SortableList
						items={sortableTests}
						onChange={setSortableItems}
						renderItem={(test) => (
							<SortableList.Item id={test.id}>
								<Box>
									<Typography variant="body1" fontWeight="bold">
										{test.name}
									</Typography>
									<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
										{test.desc}
									</Typography>
									<Typography variant="subtitle2" color="text.primary" sx={{ mt: 1 }}>
										{fCurrency(test.price)}
									</Typography>
								</Box>
								<SortableList.DragHandle />
							</SortableList.Item>
						)}
					/>
					<Grid size={{ md: 6 }} sx={{ mt: 4 }}>
						<Grid container justifyContent="start" spacing={4}>
							<Grid>
								<Button
									loading={isOrderTestsLoading}
									variant="outlined"
									color="primary"
									type="submit"
									onClick={handleUpdate}
									disabled={isOrderTestsLoading}
								>
									<span>Update</span>
								</Button>
							</Grid>
							<Grid>
								<Button color="error" variant="outlined" data-route="/dashboard/admin/tests" onClick={handleNavigate}>
									Back
								</Button>
							</Grid>
						</Grid>
					</Grid>
				</Paper>
			</Container>
		</>
	);
}
